import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Nav, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { googleCalendarService } from '../services/googleCalendarService';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';

const GoogleCalendarIntegration = () => {
    const { user } = useAuth();
    const { calendarApiEndpoints } = useEnvironment();

    const [loading, setLoading] = useState(false);
    const [checkingConnection, setCheckingConnection] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [gettingCalendarList, setGettingCalendarList] = useState(false);

    const [disconnectLoading, setDisconnectLoading] = useState(false);
    const [showGoogleLogin, setShowGoogleLogin] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [calendarList, setCalendarList] = useState([]);
    const [selectedCalendars, setSelectedCalendars] = useState([]);
    const [syncResponse, setSyncResponse] = useState(null);
    const [error, setError] = useState('');
    const [lastCode, setLastCode] = useState('');

    // Add ref to track if OAuth callback has been processed
    const oauthCallbackProcessed = useRef(false);

    // Check if user already has Google Calendar connected
    useEffect(() => {
        checkConnectionStatus();
    }, [user?.uid, calendarApiEndpoints.google]);

    // Handle OAuth callback - with proper dependencies and duplicate prevention
    useEffect(() => {
        // Only process if we have the required dependencies
        if (user?.uid && calendarApiEndpoints.google) {
            handleOAuthCallback();
        }
    }, [user?.uid, calendarApiEndpoints.google]); // Add dependencies

    const checkConnectionStatus = async () => {
        if (!user?.uid || !calendarApiEndpoints.google) {
            return;
        }

        try {
            setCheckingConnection(true);
            setLoading(true);
            const credentials = await googleCalendarService.getUserCredentials(
                user.uid,
                calendarApiEndpoints.google
            );

            if (credentials && credentials.success && credentials.hasAccessToken) {
                setIsConnected(true);
                setUserEmail(credentials.email || '');
                await fetchCalendarList();

                if ( credentials.calendars && credentials.calendars.length > 0 ) {
                    credentials.calendars.forEach((calendar) => {
                        selectedCalendars.push(calendar.calendar_id);
                    });
                    setSelectedCalendars(selectedCalendars)
                }

            }
        } catch (err) {
            console.log('No existing Google Calendar connection');
            setIsConnected(false);
        } finally {
            setCheckingConnection(false);
            setLoading(false);
        }
    };

    const handleOAuthCallback = async () => {
        // Check if we're returning from OAuth
        console.log('[handleOAuthCallback]----->');
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        console.log('[handleOAuthCallback]---lastCode-->',lastCode);
        console.log('[handleOAuthCallback]---oauthCallbackProcessed-->',oauthCallbackProcessed);
        if (code && code !== oauthCallbackProcessed.lastCode) {
            setLastCode(code);
            oauthCallbackProcessed.current = false;
            oauthCallbackProcessed.lastCode = code;
        }else{
            oauthCallbackProcessed.current = true
            oauthCallbackProcessed.lastCode = '';
        }
        console.log('[handleOAuthCallback]---code-->',code);
        console.log('[handleOAuthCallback]---state-->',state);
        console.log('[handleOAuthCallback]---oauthCallbackProcessed.current-->',oauthCallbackProcessed.current);

        // Early return if no code or if already processed
        if (!code || !state || oauthCallbackProcessed.current) {
            console.log('--BAILING--->');
            return;
        }
        console.log('--NOT BAILING--->');

        // Mark as processed immediately to prevent duplicate calls
        oauthCallbackProcessed.current = true;
        try {
            setLoading(true);

            setError('');

            const savedState = sessionStorage.getItem('google_oauth_state');
            if (state !== savedState) {
                throw new Error('Invalid state parameter');
            }

            // Get redirect URI
            const redirectUri = `${window.location.origin}${window.location.pathname}`;

            // Exchange code for tokens
            const response = await googleCalendarService.exchangeCodeForTokens(
                user.uid,
                code,
                calendarApiEndpoints.google,
                redirectUri
            );

            if (response.success) {
                setGettingCalendarList(true);
                console.log('--[handleOAuthCallback]---response.success--->');
                setIsConnected(true);
                setUserEmail(response.email || '');
                setShowGoogleLogin(true);

                window.history.replaceState({}, document.title, window.location.pathname);

                await fetchCalendarList();

                sessionStorage.removeItem('google_oauth_state');
            } else {
                setError('Failed to connect Google Calendar');
            }
        } catch (err) {
            console.error('OAuth callback error:', err);
            setError(err.message || 'Failed to complete authorization');
            // Reset the processed flag on error so user can retry if needed
            oauthCallbackProcessed.current = false;
        } finally {
            setLoading(false);
        }
    };

//http://localhost:3000/settings flowName=GeneralOAuthFlow
    const handleConnectGoogle = async () => {
        try {
            setLoading(true);
            setError('');

            // Generate random state for security
            const state = Math.random().toString(36).substring(7);
            console.log('[handleConnectGoogle]--setItem--state->',state);
            sessionStorage.setItem('google_oauth_state', state);


            // Get redirect URI
            const redirectUri = `${window.location.origin}${window.location.pathname}`;
            console.log('[handleConnectGoogle]---redirectUri-->',redirectUri);
            // Get OAuth URL from backend
            const response = await googleCalendarService.getAuthUrl(
                user.uid,
                calendarApiEndpoints.google,
                redirectUri
            );

            if (response.authUrl) {
                // Redirect to Google OAuth
                window.location.href = response.authUrl + `&state=${state}`;
            } else {
                setError('Failed to initialize Google authentication');
            }
        } catch (err) {
            console.error('Error initiating Google OAuth:', err);
            setError(err.message || 'Failed to connect to Google Calendar');
            setLoading(false);
        }
    };

    const fetchCalendarList = async () => {
        try {
            const response = await googleCalendarService.getCalendarList(
                user.uid,
                calendarApiEndpoints.google
            );

            if (response.success && response.calendars) {
                setCalendarList(response.calendars);

                // Auto-select primary calendar if available
                const primaryCalendar = response.calendars.find(cal => cal.primary);
                if (primaryCalendar) {
                    setSelectedCalendars([primaryCalendar.id]);
                }
                setGettingCalendarList(false);
            }
        } catch (err) {
            console.error('Error fetching calendar list:', err);
            setError('Failed to fetch calendar list');
        }
    };

    const handleCalendarSelection = (calendarId) => {
        setSelectedCalendars(prev => {
            if (prev.includes(calendarId)) {
                return prev.filter(id => id !== calendarId);
            } else {
                return [...prev, calendarId];
            }
        });
    };

    const handleSync = async () => {
        try {
            setSyncLoading(true);
            setSyncResponse(null);

            // Get date range for sync (e.g., 60 days from now)
            const today = new Date();
            const sixtyDaysLater = new Date(today);
            sixtyDaysLater.setDate(today.getDate() + 60);

            // Prepare calendars array with selected calendars
            const calendarsToSync = selectedCalendars.map(calendarId => {
                const calendar = calendarList.find(cal => cal.id === calendarId);
                return {
                    calendarId: calendarId,
                    calendarName: calendar?.summary || calendar?.name || calendarId
                };
            });

            const response = await googleCalendarService.syncEvents(
                user.uid,
                calendarApiEndpoints.google,
                calendarsToSync,
                today,
                sixtyDaysLater
            );

            setSyncResponse(response);
        } catch (err) {
            console.error('Error syncing calendars:', err);
            setSyncResponse({ error: err.message || 'Failed to sync calendars' });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDisconnect = async () => {
        const confirmDisconnect = window.confirm(
            'Are you sure you want to disconnect your Google Calendar? This will remove synced calendar data.'
        );

        if (!confirmDisconnect) {
            return;
        }

        try {
            setDisconnectLoading(true);
            setError('');

            await googleCalendarService.disconnect(
                user.uid,
                calendarApiEndpoints.google
            );

            // Reset state
            setIsConnected(false);
            setUserEmail('');
            setCalendarList([]);
            setSelectedCalendars([]);
            setSyncResponse({ success: true, message: 'Google Calendar disconnected successfully' });
            setShowGoogleLogin(false);
        } catch (err) {
            console.error('Error disconnecting Google Calendar:', err);
            setError(err.message || 'Failed to disconnect Google Calendar');
        } finally {
            setDisconnectLoading(false);
        }
    };

    const handleCancel = () => {
        setShowGoogleLogin(false);
        setSyncResponse(null);
        setError('');
    };

    // Google icon SVG component
    const GoogleIcon = () => (
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fillRule="evenodd">
                <path d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.6v2.2h3a8.8 8.8 0 0 0 2.6-6.6z" fill="#4285F4"/>
                <path d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.2a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z" fill="#34A853"/>
                <path d="M4 10.7a5.4 5.4 0 0 1 0-3.4V5H1a9 9 0 0 0 0 8l3-2.3z" fill="#FBBC05"/>
                <path d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.4a5.4 5.4 0 0 1 5-3.7z" fill="#EA4335"/>
            </g>
        </svg>
    );

    return (
        <div className="integration-item">
            {/* Loading indicator for connection status check */}
            {checkingConnection || gettingCalendarList && (
                <div className="d-flex align-items-center gap-2 mb-3 p-3 bg-light rounded">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted">{checkingConnection ? 'Checking Google Calendar connection... ' : 'Getting Google Calendars...'}</span>
                </div>
            )}
            {!showGoogleLogin && !isConnected && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Nav.Link
                            onClick={() => setShowGoogleLogin(true)}
                            className="p-1"
                            style={{cursor: 'pointer'}}
                        >
                            <Form.Label className="mb-0">Google Calendar</Form.Label>
                            <FontAwesomeIcon className="mb-0 ms-2" icon={faPlus}/>
                        </Nav.Link>
                    </div>
                </div>
            )}

            {!showGoogleLogin && isConnected && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                        <Form.Label className="mb-0">
                            Google Calendar Connected
                            {userEmail && <span className="text-muted ms-2">({userEmail})</span>}
                        </Form.Label>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowGoogleLogin(true)}
                            style={{ borderRadius: '56px' }}
                        >
                            Manage
                        </Button>
                    </div>
                </div>
            )}

            {showGoogleLogin && (
                <div>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <GoogleIcon />
                        <h6 className="mb-0">Google Calendar</h6>
                        {loading && <Spinner size="sm" />}
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    {!isConnected ? (
                        <>
                            <p className="text-muted small mb-3">
                                Connect your Google Calendar to sync events automatically.
                                You'll be redirected to Google to authorize access.
                            </p>

                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    disabled={loading}
                                    onClick={handleConnectGoogle}
                                    size="sm"
                                    variant="primary"
                                    style={{ borderRadius: '56px', minWidth: '120px' }}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <GoogleIcon />
                                            <span className="ms-2">Connect with Google</span>
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleCancel}
                                    style={{ borderRadius: '56px', minWidth: '80px' }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            {calendarList.length > 0 && (
                                <div className="mb-3">
                                    <Form.Label>Select calendars to get items for the next 60 days:</Form.Label>
                                    {calendarList.map(calendar => (
                                        <Form.Check
                                            key={calendar.id}
                                            type="checkbox"
                                            id={`calendar-${calendar.id}`}
                                            label={
                                                <>
                                                    {calendar.summary || calendar.name}
                                                    {calendar.primary && <span className="text-muted ms-2">(Primary)</span>}
                                                </>
                                            }
                                            checked={selectedCalendars.includes(calendar.id)}
                                            onChange={() => handleCalendarSelection(calendar.id)}
                                        />
                                    ))}
                                </div>
                            )}

                            <div className="d-flex gap-2 mb-3">
                                {selectedCalendars.length > 0 && (
                                    <Button
                                        disabled={syncLoading}
                                        onClick={handleSync}
                                        size="sm"
                                        variant="primary"
                                        style={{ borderRadius: '56px', minWidth: '80px' }}
                                    >
                                        {syncLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Syncing...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSync} className="me-2" />
                                                Select Calendars to Sync
                                            </>
                                        )}
                                    </Button>
                                )}

                                <Button
                                    disabled={disconnectLoading}
                                    onClick={handleDisconnect}
                                    size="sm"
                                    variant="outline-danger"
                                    style={{ borderRadius: '56px', minWidth: '100px' }}
                                >
                                    {disconnectLoading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Disconnecting...
                                        </>
                                    ) : (
                                        'Disconnect'
                                    )}
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleCancel}
                                    style={{ borderRadius: '56px', minWidth: '80px' }}
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Display sync response */}
                    {syncResponse && (
                        <div className="mt-3">
                            {syncResponse.error ? (
                                <Alert variant="danger">
                                    <strong>Sync Error:</strong> {syncResponse.error}
                                </Alert>
                            ) : (
                                <Alert variant="success">
                                    <strong>Success!</strong>{' '}
                                    {syncResponse.message || 'Calendar synced successfully'}
                                    {syncResponse.eventsCount && (
                                        <div className="mt-1">
                                            Synced {syncResponse.eventsCount} events from{' '}
                                            {syncResponse.calendarsProcessed} calendar(s)
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GoogleCalendarIntegration;